import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import UserPasswordUpdate from "./UserPasswordUpdate"
// import { FirebaseUserData } from "@/hooks/useFirebaseUserSettings" // Removed
import { User, UserDataInput } from "@/schemas/user" // Added

interface UserProfileCardProps {
  userData: User // Changed from FirebaseUserData
  formData: Partial<UserDataInput> // Changed from FirebaseUserData to Partial<UserDataInput>
  setFormData: (data: Partial<UserDataInput>) => void // Changed signature
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
  isLoading: boolean
  onSave: () => void
  onCancel: () => void
}

const UserProfileCard = ({
  userData,
  formData,
  setFormData,
  isEditing,
  setIsEditing,
  isLoading,
  onSave,
  onCancel,
}: UserProfileCardProps) => {
  // const [showComingSoonModal, setShowComingSoonModal] = useState(false) // Removed
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    // console.log(`Input changed: ${name} = ${value}`) // Optional: keep for debugging

    // Create a new object to ensure React detects the state change
    const updatedFormData = {
      ...formData,
      [name]: value,
    }

    // Call setFormData with the new object
    setFormData(updatedFormData)
  }

  return (
    <div className="space-y-6">
      <Card className="border border-gray-100 rounded-lg shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl">User Profile</CardTitle>
            <CardDescription>Update your personal information</CardDescription>
          </div>
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)} // Changed: directly enable editing
              variant="outline"
            >
              Edit Profile
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button onClick={onCancel} variant="outline" disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={
                  isLoading ||
                  JSON.stringify(userData) === JSON.stringify(formData)
                }
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid gap-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName || ""}
                  onChange={handleChange}
                  placeholder="First name"
                  disabled={!isEditing} // Changed
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName || ""}
                  onChange={handleChange}
                  placeholder="Last name"
                  disabled={!isEditing} // Changed
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
                placeholder="Email address"
                disabled={true} // Email remains not directly editable
              />
              <p className="text-xs text-muted-foreground">
                Email changes require verification and can't be changed directly
                here.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
                placeholder="Phone number"
                disabled={!isEditing} // Changed
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="termsLastUpdated">Terms Last Updated</Label>
              <Input
                id="termsLastUpdated"
                name="termsLastUpdated"
                value={
                  formData.termsLastUpdated instanceof Date
                    ? formData.termsLastUpdated.toLocaleDateString()
                    : formData.termsLastUpdated // If it's already a string (e.g. from manual input before Zod parse) or "Not yet accepted"
                    ? String(formData.termsLastUpdated) // Fallback to string conversion if it's some other truthy value
                    : "Not yet accepted"
                }
                disabled={true}
                readOnly
              />
              <p className="text-xs text-muted-foreground">
                This shows when you last accepted the terms and conditions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <UserPasswordUpdate isEditing={isEditing} />
      {/* ComingSoonModal removed */}
    </div>
  )
}

export default UserProfileCard
